import { Inject, Injectable } from "@angular/core";
import {Observable} from "rxjs";
import { Aircraft } from "../aircraft";
import {OrgService} from "./org.service";

@Injectable()
export class PermissionUtil {
	aircraftPermissionsMap = new Map<string, Map<number, boolean>>();
	orgPermissionsMap = new Map<number, boolean>();
	permissionsAircraftEditId: number;
	permissionsAircraftTransferId: number;
	permissionsAircraftReadId: number;
	permissionsAircraftShareId: number;
	permissionsCcdTrialId: number;
	permissionsCcdActivateId: number;
	permissionsAvdbReadId: number;
	permissionsAvdbPurchaseId: number;
	permissionsAvdbDownloadId: number;
	permissionsAvSwReadId: number;
	permissionsAvSwPurchaseId: number;
	permissionsDeviceReadId: number;
	permissionsDeviceEditId: number;
	permissionsThirdPartyId: number;

	constructor(
		@Inject("permissionsAircraftEditId") permissionsAircraftEditId: number,
		@Inject("permissionsAircraftTransferId") permissionsAircraftTransferId: number,
		@Inject("permissionsAircraftReadId") permissionsAircraftReadId: number,
		@Inject("permissionsAircraftShareId") permissionsAircraftShareId: number,
		@Inject("permissionsCcdTrialId") permissionsCcdTrialId: number,
		@Inject("permissionsCcdActivateId") permissionsCcdActivateId: number,
		@Inject("permissionsAvSwReadId") permissionsAvSwReadId: number,
		@Inject("permissionsAvSwPurchaseId") permissionsAvSwPurchaseId: number,
		@Inject("permissionsAvdbReadId") permissionsAvdbReadId: number,
		@Inject("permissionsAvdbPurchaseId") permissionsAvdbPurchaseId: number,
		@Inject("permissionsAvdbDownloadId") permissionsAvdbDownloadId: number,
		@Inject("permissionsDeviceReadId") permissionsDeviceReadId: number,
		@Inject("permissionsDeviceEditId") permissionsDeviceEditId: number,
		@Inject("permissionsThirdPartyId") permissionsThirdPartyId: number,
		private orgService: OrgService
	) {
		this.permissionsAircraftEditId = permissionsAircraftEditId;
		this.permissionsAircraftTransferId = permissionsAircraftTransferId;
		this.permissionsAircraftReadId = permissionsAircraftReadId;
		this.permissionsAircraftShareId = permissionsAircraftShareId;
		this.permissionsCcdTrialId = permissionsCcdTrialId;
		this.permissionsCcdActivateId = permissionsCcdActivateId;
		this.permissionsAvSwReadId = permissionsAvSwReadId;
		this.permissionsAvSwPurchaseId = permissionsAvSwPurchaseId;
		this.permissionsAvdbReadId = permissionsAvdbReadId;
		this.permissionsAvdbPurchaseId = permissionsAvdbPurchaseId;
		this.permissionsAvdbDownloadId = permissionsAvdbDownloadId;
		this.permissionsDeviceReadId = permissionsDeviceReadId;
		this.permissionsDeviceEditId = permissionsDeviceEditId;
		this.permissionsThirdPartyId = permissionsThirdPartyId;
	}

	hasAircraftReadPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsAircraftReadId, aircraft);
	}

	hasAircraftEditPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsAircraftEditId, aircraft);
	}

	hasTrialPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsCcdTrialId, aircraft);
	}

	hasCcdActivatePermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsCcdActivateId, aircraft);
	}

	hasSharePermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsAircraftShareId, aircraft);
	}

	hasTransferPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsAircraftTransferId, aircraft);
	}

	hasAvdbReadPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsAvdbReadId, aircraft);
	}

	hasAvdbPurchasePermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsAvdbPurchaseId, aircraft);
	}

	hasAvdbDownloadPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsAvdbDownloadId, aircraft);
	}

	hasAvSwReadPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsAvSwReadId, aircraft);
	}

	hasAvSwPurchasePermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsAvSwPurchaseId, aircraft);
	}

	hasDeviceReadPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsDeviceReadId, aircraft);
	}

	hasDeviceEditPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsDeviceEditId, aircraft);
	}

	hasThirdPartyPermission(aircraft?: Aircraft): Observable<boolean> {
		return this.hasPermission(this.permissionsThirdPartyId, aircraft);
	}

	/**
	 * Checks if a user is authorized for a given action and (optional) given
	 * aircraft.
	 *
	 * If the user is in their personal context:
	 * 	- Return true for non-master aircraft (and no aircraft)
	 * 	- Check permissions for master aircraft
	 * If the user is in an organization context:
	 * 	- Check org permissions for non-master aircraft
	 * 	- Check org+aircraft permissions for master aircraft
	 *
	 * @param permissionId ID of the necessary permission
	 * @param aircraft aircraft which contains permission info if master aircraft
	 */
	hasPermission(permissionId: number, aircraft?: Aircraft): Observable<boolean> {
		let isMasterAircraft = false;
		let hasAircraftPermission = true;
		if (aircraft != null
			&& aircraft.permissions != null
			&& aircraft.permissions.rolePermissions != null
			&& aircraft.permissions.rolePermissions.permissions != null
			&& aircraft.masterAircraftUuid != null) {
			isMasterAircraft = true;
			hasAircraftPermission = aircraft.permissions.rolePermissions.permissions.some(p => p.id === permissionId);
		}

		if (this.orgService.isPersonalContext) {
			return Observable.of(hasAircraftPermission);
		}

		// Check permissions from org and aircraft
		if (isMasterAircraft) {
			const cachedAircraft = this.aircraftPermissionsMap.get(aircraft.masterAircraftUuid);
			if (cachedAircraft != null) {
				const cachedPermission = cachedAircraft.get(permissionId);
				if (cachedPermission != null) {
					return Observable.of(cachedPermission);
				}
			} else {
				this.aircraftPermissionsMap.set(aircraft.masterAircraftUuid, new Map<number, boolean>());
			}

			return this.orgService.hasAircraftAuthorization({
				permissionId,
				masterAircraftUuids: [aircraft.masterAircraftUuid]
			})
				.do((authorized) => this.aircraftPermissionsMap.get(aircraft.masterAircraftUuid).set(permissionId, authorized))
				.take(1);
		}

		// If not master aircraft (or no aircraft at all), check permissions from org
		if (this.orgPermissionsMap.has(permissionId)) {
			return Observable.of(this.orgPermissionsMap.get(permissionId));
		}
		return this.orgService.hasOrgAuthorization(permissionId)
			.do((authorized) => this.orgPermissionsMap.set(permissionId, authorized))
			.take(1);
	}
}
